import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Container, Card, Row, Col, Form, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";

const AddFinancialYear = () => {
  const routeLocation = useLocation();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    fy_name: "",
    start_date: "",
    end_date: "",
    is_active: false,
    is_closed: false,
    opening_balance: "",
  });

  const [currentBalance, setCurrentBalance] = useState("");
  const [totalIncome, setTotalIncome] = useState("");
  const [totalExpense, setTotalExpense] = useState("");

  const fetchSummary = async (fy_id) => {
    try {
      const res = await axios.get(`${baseUrl}/fy/summary`, {
        params: { school_id: userObj.school_id, fy_id },
      });

      const data = res.data || {};
      const income = data.total_income != null ? data.total_income : 0;
      const expense = data.total_expense != null ? data.total_expense : 0;
      const opening = data.opening_balance != null ? data.opening_balance : 0;

      const computedCurrent =
        data.current_balance != null
          ? data.current_balance
          : opening + income - expense;

      setCurrentBalance(String(computedCurrent));
      setTotalIncome(String(income));
      setTotalExpense(String(expense));
    } catch (err) {
      console.error("Failed to fetch summary", err);
    }
  };

  const fetchCurrentFYOpeningBalance = async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/financialyear/current-opening-balance/`,
        {
          params: { school_id: userObj.school_id },
        }
      );

      const data = res.data || {};

      setForm((prev) => ({
        ...prev,
        opening_balance: String(data.current_balance ?? 0),
      }));
    } catch (error) {
      console.error("Failed to fetch current FY opening balance", error);
      setForm((prev) => ({
        ...prev,
        opening_balance: "0",
      }));
    }
  };

  useEffect(() => {
    if (routeLocation.state?.year) {
      const fy = routeLocation.state.year;
      setEditId(fy.fy_id);

      setForm({
        fy_name: fy.fy_name || "",
        start_date: fy.start_date || "",
        end_date: fy.end_date || "",
        is_active: !!fy.is_active,
        is_closed: !!fy.is_closed,
        opening_balance:
          fy.opening_balance != null ? String(fy.opening_balance) : "",
      });

      setCurrentBalance(
        fy.current_balance != null ? String(fy.current_balance) : ""
      );
      setTotalIncome(fy.total_income != null ? String(fy.total_income) : "");
      setTotalExpense(fy.total_expense != null ? String(fy.total_expense) : "");

      if (
        fy.current_balance == null &&
        fy.total_income == null &&
        fy.total_expense == null &&
        fy.fy_id
      ) {
        fetchSummary(fy.fy_id);
      }

      window.history.replaceState({}, document.title);
    } else {
      setEditId(null);
      setForm({
        fy_name: "",
        start_date: "",
        end_date: "",
        is_active: false,
        is_closed: false,
        opening_balance: "",
      });
      setCurrentBalance("");
      setTotalIncome("");
      setTotalExpense("");
      fetchCurrentFYOpeningBalance();
    }
  }, [routeLocation]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;

    if (id === "fy_name") {
      if (/^[0-9-]{0,20}$/.test(value)) {
        setForm((prev) => ({ ...prev, fy_name: value }));
      }
      return;
    }

    if (id === "opening_balance") {
      if (/^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
        setForm((prev) => ({ ...prev, opening_balance: value }));
      }
      return;
    }

    if (type === "checkbox") {
      if (id === "is_closed") {
        setForm((prev) => ({
          ...prev,
          is_closed: checked,
          is_active: checked ? false : prev.is_active,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [id]: checked }));
      return;
    }

    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleReset = () => {
    if (editId) {
      setForm({
        fy_name: "",
        start_date: "",
        end_date: "",
        is_active: false,
        is_closed: false,
        opening_balance: "",
      });
      setCurrentBalance("");
      setTotalIncome("");
      setTotalExpense("");
      setEditId(null);
    } else {
      setForm({
        fy_name: "",
        start_date: "",
        end_date: "",
        is_active: false,
        is_closed: false,
        opening_balance: "",
      });
      setCurrentBalance("");
      setTotalIncome("");
      setTotalExpense("");
      fetchCurrentFYOpeningBalance();
    }
  };

  const validateForm = () => {
    if (!form.fy_name?.trim()) {
      toast.error("Financial year is required");
      return false;
    }

    if (!form.start_date) {
      toast.error("Start date is required");
      return false;
    }

    if (!form.end_date) {
      toast.error("End date is required");
      return false;
    }

    if (new Date(form.start_date) >= new Date(form.end_date)) {
      toast.error("Start date must be less than End date");
      return false;
    }

    if (form.opening_balance === "") {
      toast.error("Opening balance is required");
      return false;
    }

    const ob = Number(form.opening_balance);
    if (isNaN(ob) || ob < 0) {
      toast.error("Opening balance must be a non-negative number");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      fy_id: editId,
      school_id: userObj.school_id,
      fy_name: form.fy_name,
      start_date: form.start_date,
      end_date: form.end_date,
      is_active: form.is_active,
      is_closed: form.is_closed,
      opening_balance: Number(form.opening_balance || 0),
      created_by: userObj.user_name,
      action: editId ? "UPDATE" : "CREATE",
    };

    try {
      const res = await axios.post(
        `${baseUrl}/financialyearprocedure/`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const returnedFyId = editId || res.data?.data?.fy_id || null;

      if (returnedFyId != null) {
        await axios.post(`${baseUrl}/fy/openingbalance`, {
          school_id: userObj.school_id,
          fy_id: returnedFyId,
          opening_balance: Number(form.opening_balance || 0),
          created_by: userObj.user_name,
        });
      }

      toast.success(
        editId ? "Record Updated Successfully" : "Record Added Successfully"
      );
      window.history.back();
    } catch (error) {
      console.error("Error:", error?.response?.data || error.message);

      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        "Unexpected error occurred";

      if (String(msg).toLowerCase().includes("overlap")) {
        toast.error("Financial year dates overlap with existing year");
      } else if (
        String(msg).toLowerCase().includes("already") ||
        String(msg).toLowerCase().includes("duplicate") ||
        error?.response?.data?.pgcode === "23505" ||
        error?.response?.data?.pgcode == 23505
      ) {
        toast.error("Record already exist");
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">Financial Year Details</h6>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Financial Year <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="text"
                        id="fy_name"
                        value={form.fy_name}
                        placeholder="e.g. 2025-2026"
                        maxLength={20}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Start Date <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="date"
                        id="start_date"
                        value={form.start_date}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        End Date <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="date"
                        id="end_date"
                        value={form.end_date}
                        min={form.start_date || ""}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Opening Balance <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="number"
                        step="0.01"
                        min="0"
                        id="opening_balance"
                        value={form.opening_balance}
                        onChange={handleInputChange}
                        disabled={!!editId}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {editId && (
                  <>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Current Balance</Form.Label>
                          <Form.Control
                            type="text"
                            value={currentBalance}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Total Income</Form.Label>
                          <Form.Control
                            type="text"
                            value={totalIncome}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Total Expense</Form.Label>
                          <Form.Control
                            type="text"
                            value={totalExpense}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </div>
                    </Col>
                  </>
                )}
              </Row>

              <Row>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Check
                      type="checkbox"
                      id="is_active"
                      label="Active"
                      checked={form.is_active}
                      onChange={handleInputChange}
                      disabled={form.is_closed}
                    />
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Check
                      type="checkbox"
                      id="is_closed"
                      label="Closed"
                      checked={form.is_closed}
                      onChange={handleInputChange}
                    />
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-info clearBtn"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </div>

                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-danger secondaryBtn me-2"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="btn-success primaryBtn"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddFinancialYear;